/* eslint-disable no-console */
import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
} from 'react';

import axios, { Canceler } from 'axios';
import fileSaver from 'file-saver';
import filesize from 'filesize';
import { v4 as uuidv4 } from 'uuid';

import api from '~/services/api';
import { IProcessFile } from '~/store/modules/processes/types';

export interface IFile {
  id: string;
  name: string;
  key: string;
  readableSize?: string;
  uploaded?: boolean;
  preview: string;
  file: File | null;
  progress?: number;
  error?: boolean;
  url: string;
  formFieldId?: string;
}

interface IFileContextData {
  numberOfFilesToUpload: number;
  deletingFile: string | null;
  uploadedFiles: IFile[];
  historyFiles: IFile[];
  loadingHistoryFiles: boolean;
  handleUpload(files: File[], formFieldId?: string): void;
  handleUploadThumbnail(
    file: File,
    key: string | undefined,
    onUploadProgress?: (progress: number) => void,
  ): void;
  handleDelete(keys: string[]): void;
  handleGetHistory(processInstanceId: string): void;
  handleDownloadFile(name: string): void;
  resetUploadedFiles(): void;
  resetHistoryFiles(): void;
  getObject(fileName: string): any;
}

const FileContext = createContext<IFileContextData>({} as IFileContextData);

const FileProvider: React.FC = ({ children }) => {
  const [numberOfFilesToUpload, setNumberOfFilesToUpload] = useState(0);
  const [deletingFile, setDeletingFile] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<IFile[]>([]);
  const [historyFiles, setHistoryFiles] = useState<IFile[]>([]);
  const [loadingHistoryFiles, setLoadingHistoryFiles] = useState<boolean>(
    false,
  );

  const { CancelToken } = axios;

  const uploadInProgress: { [key: string]: Canceler } = {};

  const AWSS3 = axios.create({
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  useEffect(() => {
    return () => {
      uploadedFiles.forEach((file) => URL.revokeObjectURL(file.preview));
    };
  });

  const resetHistoryFiles = useCallback(() => {
    setHistoryFiles([]);
  }, []);

  const resetUploadedFiles = useCallback(() => {
    setUploadedFiles([]);
  }, [setUploadedFiles]);

  const updateFile = useCallback((id, data) => {
    setUploadedFiles((state) =>
      state.map((file) => (file.id === id ? { ...file, ...data } : file)),
    );
  }, []);

  const processUpload = useCallback(
    async (uploadedFile: IFile) => {
      try {
        const data = new FormData();

        if (uploadedFile.file) {
          data.append('file', uploadedFile.file, uploadedFile.key);
        }

        const response = await api.post('/upload/signS3', {
          fileName: uploadedFile.key,
          fileType: uploadedFile.file?.type,
        });

        const { signedRequest, url } = response.data.data;

        axios
          .put(signedRequest, uploadedFile.file, {
            headers: {
              'Content-Type': uploadedFile.file?.type,
            },
            onUploadProgress: (progressEvent) => {
              const progress: number = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );

              console.log(
                `A imagem ${uploadedFile.name} está ${progress}% carregada... `,
              );

              updateFile(uploadedFile.id, { progress });
            },
          })
          .then((response) => {
            console.log(
              `A imagem ${uploadedFile.name} já foi enviada para o servidor!`,
            );

            setNumberOfFilesToUpload(numberOfFilesToUpload - 1);

            updateFile(uploadedFile.id, {
              uploaded: true,
              id: response.data._id,
              url,
            });
          })
          .catch((err) => {
            console.error(
              `Houve um problema para fazer upload da imagem ${uploadedFile.name} no servidor AWS`,
            );
            console.log(err);

            setNumberOfFilesToUpload(numberOfFilesToUpload - 1);

            updateFile(uploadedFile.id, {
              error: true,
            });
          });
      } catch (err) {
        console.log(err);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateFile],
  );

  const getS3Credentials = useCallback(async () => {
    const response = await api.get('/upload/s3-credentials');

    return response.data;
  }, []);

  const handleUpload = useCallback(
    (files: File[], formFieldId?: string) => {
      const newUploadedFiles: IFile[] = files.map((file: File) => ({
        file,
        id: uuidv4(),
        name: file.name,
        key: `${new Date().getTime()}-${file.name}`,
        readableSize: filesize(file.size),
        preview: URL.createObjectURL(file),
        progress: 0,
        uploaded: false,
        error: false,
        url: '',
        formFieldId,
      }));

      setNumberOfFilesToUpload(newUploadedFiles.length);
      setUploadedFiles((state) => state.concat(newUploadedFiles));
      newUploadedFiles.forEach(processUpload);
    },
    [processUpload],
  );

  const handleUploadThumbnail = async (
    file: File,
    key: string,
    onUploadProgress?: (progress: number) => void,
  ) => {
    const credential = await getS3Credentials();
    const url = `https://${credential.region}.amazonaws.com/${credential.bucket}/`;
    const form: FormData = new FormData();
    let returnUrl = null;
    form.append('key', key);
    form.append('AWSAccessKeyId', credential.key);
    form.append('acl', credential.acl);
    form.append('policy', credential.policy);
    form.append('signature', credential.signature);
    form.append('Content-Type', file.type);
    form.append('file', file);
    await AWSS3.post(url, form, {
      cancelToken: new CancelToken((c) => {
        uploadInProgress[key] = c;
      }),
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress) {
          onUploadProgress(
            Math.round((progressEvent.loaded / progressEvent.total) * 100),
          );
        }
      },
    })
      .then(() => {
        returnUrl = url + encodeURIComponent(key);
      })
      .catch(() => {});
    return returnUrl;
  };

  const handleDelete = useCallback(async (keys: string[]) => {
    if (keys.length === 1) {
      setDeletingFile(keys[0]);
    }

    await api.delete(`upload`, {
      params: {
        fileNames:
          keys.length > 0
            ? keys
            : uploadedFiles.map((uploadedFile) => uploadedFile.key),
      },
    });

    keys.forEach((key) => {
      setUploadedFiles((state) => state.filter((file) => file.key !== key));
    });

    setDeletingFile(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetHistory = useCallback(async (processInstanceId: string) => {
    if (!processInstanceId) {
      return;
    }
    setLoadingHistoryFiles(true);
    const { data } = await api.get<IProcessFile[]>(
      `/processFiles/rootAndExecutionId/${processInstanceId}`,
    );
    const processFiles = data;

    const files: IFile[] = processFiles.map((file) => ({
      file: null,
      id: file.id ?? uuidv4(),
      name: file.file_name,
      key: file.s3_file_name,
      preview: file.path,
      progress: 0,
      uploaded: true,
      error: false,
      url: file.path,
      formFieldId: file.form_field_id,
    }));
    setHistoryFiles(files);
    setLoadingHistoryFiles(false);
  }, []);

  const handleDownloadFile = async (fileName: string) => {
    const object = await getObject(fileName);
    fileSaver.saveAs(object.data);
  };

  const getObject = async (fileName: string): Promise<any> => {
    const response = await api.get('/upload/get-object', {
      params: { fileName },
    });

    return response.data;
  };

  return (
    <FileContext.Provider
      value={{
        numberOfFilesToUpload,
        deletingFile,
        uploadedFiles,
        historyFiles,
        loadingHistoryFiles,
        handleUpload,
        handleUploadThumbnail,
        handleDelete,
        handleDownloadFile,
        handleGetHistory,
        resetUploadedFiles,
        resetHistoryFiles,
        getObject,
      }}
    >
      {children}
    </FileContext.Provider>
  );
};

function useFiles(): IFileContextData {
  const context = useContext(FileContext);

  if (!context) {
    throw new Error('useFiles must be used within FileProvider');
  }

  return context;
}

export { FileProvider, useFiles };
